import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatRupiah, formatDate, errMsg } from "@/lib/api";
import { ArrowLeft, Plus } from "lucide-react";
import SpkPanel from "@/components/SpkPanel";

const STATUS = ["Persiapan","Produksi","Load-in","Event","Bongkar","Selesai"];
const PHASES = ["H-14","H-7","H-5","H-3","H-1","H","H+1"];

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [payment, setPayment] = useState({ amount: 0, method: "Transfer", note: "" });
  const [checkTask, setCheckTask] = useState({ phase: "H-7", task: "", responsible: "" });

  async function load() {
    const { data } = await api.get(`/projects/${id}`);
    if (data.restricted) setTab(t => (t === "overview" ? "spk" : t));
    setP(data);
  }
  useEffect(() => { load(); }, [id]);
  if (!p) return <div className="p-6">Memuat...</div>;

  async function updateStatus(s) {
    await api.patch(`/projects/${id}/status`, { status: s });
    await load();
  }

  async function addPayment(e) {
    e.preventDefault();
    try {
      await api.post("/payments", { project_id: id, ...payment, amount: Number(payment.amount) });
      setPayment({ amount: 0, method: "Transfer", note: "" });
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  async function addCheck(e) {
    e.preventDefault();
    await api.post(`/projects/${id}/checklist`, checkTask);
    setCheckTask({ phase: "H-7", task: "", responsible: "" });
    await load();
  }

  async function toggleCheck(c) {
    await api.patch(`/checklist/${c.id}`, { done: !c.done });
    await load();
  }

  return (
    <div className="p-4 sm:p-8 space-y-6">
      <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-[#B87333]"><ArrowLeft size={16} />Kembali</Link>

      <div className="card-editorial p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#B87333]">{p.segment} · {p.number}</div>
            <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">{p.name}</h1>
            <p className="text-sm text-[#536263]">{p.client?.name} · {p.venue?.name} · {formatDate(p.event_date)}</p>
          </div>
          <select value={p.status} onChange={e => updateStatus(e.target.value)} className="border border-[#E2DDD3] rounded-lg px-3 h-10" data-testid="project-status-select">
            {STATUS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {!p.restricted && <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          <StatCard label="Kontrak" val={formatRupiah(p.contract_value)} />
          <StatCard label="Terbayar" val={formatRupiah(p.total_paid)} />
          <StatCard label="Sisa Tagihan" val={formatRupiah(p.remaining_bill)} />
          <StatCard label="Saldo Project" val={formatRupiah(p.balance)} tone={p.balance < 0 ? "danger" : "ok"} />
          <StatCard label="Biaya Produksi" val={formatRupiah(p.total_spent)} tone="danger" />
          <StatCard label={`Laba Kotor · ${p.profit_status}`} val={p.profit == null ? "-" : `${formatRupiah(p.profit)} (${p.margin}%)`} tone={p.profit < 0 ? "danger" : "ok"} />
        </div>}
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {(p.restricted ? [["spk","SPK Zona"],["checklist","Checklist H-H"],["workers","Tim"]] : [["overview","Keuangan"],["spk","SPK Zona"],["checklist","Checklist H-H"],["workers","Tim & Upah"]]).map(([k,l]) =>
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`chip whitespace-nowrap ${tab === k ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{l}</button>
        )}
      </div>

      {tab === "overview" && !p.restricted && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-editorial p-5">
            <h3 className="font-serif-display text-lg font-semibold mb-3">Pembayaran Klien</h3>
            <form onSubmit={addPayment} className="flex gap-2 mb-3">
              <input type="number" required placeholder="Nominal" value={payment.amount} onChange={e => setPayment({...payment, amount: e.target.value})}
                className="flex-1 border border-[#E2DDD3] rounded-lg px-3 h-11" />
              <input placeholder="Catatan" value={payment.note} onChange={e => setPayment({...payment, note: e.target.value})}
                className="flex-1 border border-[#E2DDD3] rounded-lg px-3 h-11" />
              <button className="btn-primary" data-testid="add-payment-btn"><Plus size={18} /></button>
            </form>
            {err && <div className="text-sm text-red-700 mb-2">{err}</div>}
            <div className="space-y-2">
              {p.payments.map(x => (
                <div key={x.id} className="flex justify-between p-2 bg-[#FAF7F2] rounded">
                  <div><div className="text-sm font-medium">{x.note || "Pembayaran"}</div><div className="text-xs text-[#8A999A]">{formatDate(x.date)}</div></div>
                  <div className="font-mono-num text-[#0F5257] font-semibold">{formatRupiah(x.amount)}</div>
                </div>
              ))}
              {p.payments.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada pembayaran</p>}
            </div>
          </div>

          <div className="card-editorial p-5">
            <h3 className="font-serif-display text-lg font-semibold mb-3">Pengeluaran Project (Disetujui)</h3>
            <div className="space-y-2">
              {p.expenses.map(e => (
                <div key={e.id} className="flex justify-between p-2 bg-[#FAF7F2] rounded">
                  <div><div className="text-sm font-medium">{e.description}</div><div className="text-xs text-[#8A999A]">{e.category}</div></div>
                  <div className="font-mono-num text-red-700 font-semibold">-{formatRupiah(e.amount)}</div>
                </div>
              ))}
              {p.expenses.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada pengeluaran</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "spk" && <SpkPanel projectId={id} />}

      {tab === "checklist" && (
        <div className="card-editorial p-5">
          <h3 className="font-serif-display text-lg font-semibold mb-3">Checklist Produksi</h3>
          <form onSubmit={addCheck} className="flex gap-2 mb-4 flex-wrap">
            <select value={checkTask.phase} onChange={e => setCheckTask({...checkTask, phase: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11">
              {PHASES.map(ph => <option key={ph}>{ph}</option>)}
            </select>
            <input required placeholder="Tugas" value={checkTask.task} onChange={e => setCheckTask({...checkTask, task: e.target.value})}
              className="flex-1 min-w-[200px] border border-[#E2DDD3] rounded-lg px-3 h-11" />
            <input placeholder="PIC" value={checkTask.responsible} onChange={e => setCheckTask({...checkTask, responsible: e.target.value})}
              className="w-32 border border-[#E2DDD3] rounded-lg px-3 h-11" />
            <button className="btn-primary"><Plus size={18} /></button>
          </form>
          <div className="space-y-4">
            {PHASES.map(ph => {
              const items = p.checklist.filter(c => c.phase === ph);
              if (!items.length) return null;
              return (
                <div key={ph}>
                  <div className="text-xs uppercase tracking-wider text-[#B87333] mb-2">{ph}</div>
                  <div className="space-y-1">
                    {items.map(c => (
                      <label key={c.id} className="flex items-center gap-3 p-2 bg-[#FAF7F2] rounded cursor-pointer">
                        <input type="checkbox" checked={c.done} onChange={() => toggleCheck(c)} data-testid={`check-${c.id}`} />
                        <span className={`flex-1 text-sm ${c.done ? "line-through text-[#8A999A]" : ""}`}>{c.task}</span>
                        <span className="text-xs text-[#8A999A]">{c.responsible}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            {p.checklist.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada checklist</p>}
          </div>
        </div>
      )}

      {tab === "workers" && (
        <div className="card-editorial p-5">
          <h3 className="font-serif-display text-lg font-semibold mb-3">Pembayaran Tim & Freelancer</h3>
          <p className="text-sm text-[#8A999A]">Tim: {p.workers.length} orang</p>
          <div className="space-y-2 mt-3">
            {p.workers.map(w => (
              <div key={w.id} className="flex justify-between items-center p-3 bg-[#FAF7F2] rounded">
                <div><div className="font-medium text-sm">{w.worker_name}</div><div className="text-xs text-[#8A999A]">{w.role}</div></div>
                <div className="text-right">
                  {w.amount !== undefined && <div className="font-mono-num font-semibold">{formatRupiah(w.amount)}</div>}
                  <span className={`chip ${w.status === "Dibayar" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>{w.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, val, tone }) {
  return (
    <div className="p-3 bg-[#FAF7F2] rounded-lg">
      <div className="text-[10px] uppercase tracking-wider text-[#B87333]">{label}</div>
      <div className={`font-mono-num text-base sm:text-lg font-semibold ${tone === "danger" ? "text-red-700" : "text-[#0F5257]"}`}>{val}</div>
    </div>
  );
}
